import { type UserListQuery, type UserLookups } from '@gb-ticket/shared-types';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { adminPanelClassName } from '@/platform/features/admin-panel';

type UsersFiltersProps = {
  query: UserListQuery;
  lookups: UserLookups | undefined;
  searchInput: string;
  total: number | undefined;
  hasFilters: boolean;
  onSearchInputChange: (value: string) => void;
  onChange: (patch: Partial<UserListQuery>) => void;
  onReset: () => void;
};

export function UsersFilters({
  query,
  lookups,
  searchInput,
  total,
  hasFilters,
  onSearchInputChange,
  onChange,
  onReset,
}: UsersFiltersProps) {
  const roleName = lookups?.roles.find((role) => role.code === query.role)?.name ?? query.role;
  const departmentName =
    lookups?.departments.find((department) => department.id === query.departmentId)?.name ??
    'Abteilung';
  const teamName = lookups?.teams.find((team) => team.id === query.teamId)?.name ?? 'Team';

  return (
    <div className={`${adminPanelClassName} flex flex-col gap-3`}>
      <div className="flex items-start justify-between gap-3">
        <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
            <Label htmlFor="user-search">Suche</Label>
            <Input
              id="user-search"
              value={searchInput}
              onChange={(event) => onSearchInputChange(event.target.value)}
              placeholder="Name oder E-Mail"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-role">Berechtigungsgruppe</Label>
            <Select
              value={query.role ?? 'all'}
              onValueChange={(value) =>
                onChange({ role: value === 'all' ? undefined : value, page: 1 })
              }
            >
              <SelectTrigger id="user-role" className="w-full">
                <SelectValue placeholder="Alle Berechtigungsgruppen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Berechtigungsgruppen</SelectItem>
                {(lookups?.roles ?? []).map((role) => (
                  <SelectItem key={role.id} value={role.code}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-department">Abteilung</Label>
            <Select
              value={query.departmentId ?? 'all'}
              onValueChange={(value) =>
                onChange({ departmentId: value === 'all' ? undefined : value, page: 1 })
              }
            >
              <SelectTrigger id="user-department" className="w-full">
                <SelectValue placeholder="Alle Abteilungen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Abteilungen</SelectItem>
                {(lookups?.departments ?? []).map((department) => (
                  <SelectItem key={department.id} value={department.id}>
                    {department.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-team">Team</Label>
            <Select
              value={query.teamId ?? 'all'}
              onValueChange={(value) =>
                onChange({ teamId: value === 'all' ? undefined : value, page: 1 })
              }
            >
              <SelectTrigger id="user-team" className="w-full">
                <SelectValue placeholder="Alle Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Teams</SelectItem>
                {(lookups?.teams ?? []).map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-status">Status</Label>
            <Select
              value={query.status}
              onValueChange={(value) =>
                onChange({
                  status: value as UserListQuery['status'],
                  page: 1,
                })
              }
            >
              <SelectTrigger id="user-status" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="inactive">Deaktiviert</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {total !== undefined ? (
          <p className="shrink-0 text-sm text-muted-foreground">{total} Benutzer</p>
        ) : null}
      </div>
      {hasFilters ? (
        <div className="flex flex-wrap items-center gap-2">
          {query.q ? (
            <FilterChip
              label={query.q}
              onClear={() => {
                onSearchInputChange('');
                onChange({ q: undefined, page: 1 });
              }}
            />
          ) : null}
          {query.role ? (
            <FilterChip
              label={roleName ?? query.role}
              onClear={() => onChange({ role: undefined, page: 1 })}
            />
          ) : null}
          {query.departmentId ? (
            <FilterChip
              label={departmentName}
              onClear={() => onChange({ departmentId: undefined, page: 1 })}
            />
          ) : null}
          {query.teamId ? (
            <FilterChip label={teamName} onClear={() => onChange({ teamId: undefined, page: 1 })} />
          ) : null}
          {query.status !== 'all' ? (
            <FilterChip
              label={query.status === 'active' ? 'Aktiv' : 'Deaktiviert'}
              onClear={() => onChange({ status: 'all', page: 1 })}
            />
          ) : null}
          <Button type="button" variant="ghost" size="sm" onClick={onReset}>
            Zurücksetzen
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <Badge variant="secondary" asChild>
      <button type="button" onClick={onClear}>
        {label}
      </button>
    </Badge>
  );
}
